import { useCallback, useSyncExternalStore } from 'react';

/**
 * Tailwind's `md` breakpoint, phrased as its negation.
 *
 * `md:` applies from `min-width: 48rem`, so "below md" is everything under 768 px. The
 * fractional bound is what keeps the two halves from both matching at exactly 767.5 px on a
 * fractional-DPI screen.
 */
export const MEDIA_BELOW_MD = '(max-width: 767.98px)';

/**
 * Whether a CSS media query currently matches, as React state.
 *
 * Layout that only needs to *look* different at a breakpoint belongs in a `md:` class; this is
 * for the cases where a component has to *compute* differently — a table whose column widths
 * feed a `colgroup` and a `min-width` in JavaScript cannot express "narrower on phones" in a
 * class name.
 *
 * Built on `useSyncExternalStore` rather than `useState` + an effect so the first render already
 * has the right answer: an effect would paint the desktop layout once and correct it on the next
 * frame, which on a phone is a visible jump of the whole table.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const list = window.matchMedia(query);
      list.addEventListener('change', onStoreChange);
      return () => list.removeEventListener('change', onStoreChange);
    },
    [query]
  );

  const getSnapshot = useCallback(() => window.matchMedia(query).matches, [query]);

  // The app is client-rendered, so this only ever runs in a non-browser test environment; a
  // desktop-width answer is the safer default there.
  const getServerSnapshot = useCallback(() => false, []);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
